import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { Encuesta, Pregunta } from '../../../../shared/model/encuesta.model';
import { EncuestasService } from '../../../../shared/services/encuestas.service';
import { PreguntasEncuestasService } from '../../../../shared/services/preguntas-encuestas.service';
import { environment } from '../../../../../environments/environment';

const Swal = require('sweetalert2');

interface RespuestaPregunta {
  pregunta_id: number;
  tipo: string;
  respuesta_texto?: string;
  respuesta_opcion?: number;
  respuesta_opciones?: number[];
  respuesta_escala?: number;
  respuesta_si_no?: boolean;
}

@Component({
  selector: 'app-responder-encuesta',
  templateUrl: './responder-encuesta.component.html',
  styleUrls: ['./responder-encuesta.component.scss']
})
export class ResponderEncuestaComponent implements OnInit {

  encuesta: Encuesta | null = null;
  isLoading = true;
  isEnviando = false;
  enviado = false;
  encuestaId = 0;
  nit = '';
  nitEmpresa = '';
  errorMsg = '';
  nombre_respondente = '';
  placa_vehiculo = '';
  placaDesdeQr = false;
  listadoPlacas: string[] = [];

  respuestas: RespuestaPregunta[] = [];
  submitted = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private encuestasService: EncuestasService,
    private preguntasService: PreguntasEncuestasService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.nitEmpresa = this.route.snapshot.paramMap.get('nitEmpresa') || '';
    this.nit = this.route.snapshot.paramMap.get('nit') || '';
    this.encuestaId = Number(this.route.snapshot.paramMap.get('id'));
    // Si viene de un QR por placa individual
    const placaParam = this.route.snapshot.queryParamMap.get('placa') || '';
    this.placa_vehiculo = placaParam;
    this.placaDesdeQr = !!placaParam;

    // Si viene de un QR con listado de placas
    const placasParam = this.route.snapshot.queryParamMap.get('placas') || '';
    if (placasParam) {
      this.listadoPlacas = placasParam.split(',').map(p => p.trim().toUpperCase()).filter(p => p);
    }
    this.cargarEncuesta();
  }

  cargarEncuesta(): void {
    this.isLoading = true;
    this.encuestasService.getEncuestaPublica(this.nit, this.encuestaId).subscribe({
      next: (data) => {
        this.encuesta = data;
        this.preguntasService.getPreguntasPublicas(this.nit, this.encuestaId).subscribe({
          next: async (preguntas) => {
            // Cargar opciones de selección desde el endpoint público
            for (const p of preguntas) {
              if ((p.tipo === 'seleccion_unica' || p.tipo === 'seleccion_multiple') && p.id) {
                try {
                  p.opciones = await lastValueFrom(
                    this.preguntasService.getOpcionesPublicas(this.nit, this.encuestaId, p.id)
                  );
                } catch { p.opciones = p.opciones || []; }
              }
            }
            this.encuesta!.preguntas = preguntas;
            this.inicializarRespuestas();
            this.isLoading = false;
          },
          error: () => {
            this.inicializarRespuestas();
            this.isLoading = false;
          }
        });
      },
      error: (err) => {
        this.isLoading = false;
        this.encuesta = null;
        this.errorMsg = (err?.status === 401 || err?.status === 403)
          ? 'No tienes permiso para acceder a esta encuesta.'
          : 'La encuesta no fue encontrada o no está disponible.';
      }
    });
  }

  inicializarRespuestas(): void {
    if (!this.encuesta?.preguntas) return;
    this.respuestas = this.encuesta.preguntas.map(p => ({
      pregunta_id: p.id!,
      tipo: p.tipo,
      respuesta_texto: '',
      respuesta_opcion: undefined,
      respuesta_opciones: [],
      respuesta_escala: undefined,
      respuesta_si_no: undefined
    }));
  }

  limpiarFormulario(): void {
    this.nombre_respondente = '';
    // Solo limpiar placa si no vino precargada desde el QR
    if (!this.route.snapshot.queryParamMap.get('placa')) {
      this.placa_vehiculo = '';
    }
    this.inicializarRespuestas();
  }

  getRespuesta(index: number): RespuestaPregunta {
    return this.respuestas[index];
  }

  toggleOpcionMultiple(index: number, opcionId: number): void {
    const r = this.respuestas[index];
    if (!r.respuesta_opciones) r.respuesta_opciones = [];
    const pos = r.respuesta_opciones.indexOf(opcionId);
    if (pos === -1) {
      r.respuesta_opciones.push(opcionId);
    } else {
      r.respuesta_opciones.splice(pos, 1);
    }
  }

  isOpcionSeleccionada(index: number, opcionId: number): boolean {
    return this.respuestas[index]?.respuesta_opciones?.includes(opcionId) ?? false;
  }

  esValido(): boolean {
    if (!this.encuesta?.preguntas) return false;
    return this.encuesta.preguntas.every((p, i) => {
      const r = this.respuestas[i];
      if (!r) return false;
      switch (p.tipo) {
        case 'abierta': return !!r.respuesta_texto?.trim();
        case 'seleccion_unica': return r.respuesta_opcion != null;
        case 'seleccion_multiple': return (r.respuesta_opciones?.length ?? 0) > 0;
        case 'escala': return r.respuesta_escala != null;
        case 'si_no': return r.respuesta_si_no != null;
        default: return true;
      }
    });
  }

  esRespondida(index: number): boolean {
    const r = this.respuestas[index];
    const p = this.encuesta?.preguntas?.[index];
    if (!r || !p) return false;
    switch (p.tipo) {
      case 'abierta': return !!r.respuesta_texto?.trim();
      case 'seleccion_unica': return r.respuesta_opcion != null;
      case 'seleccion_multiple': return (r.respuesta_opciones?.length ?? 0) > 0;
      case 'escala': return r.respuesta_escala != null;
      case 'si_no': return r.respuesta_si_no != null;
      default: return false;
    }
  }

  getTipoLabel(tipo: string): string {
    const labels: Record<string, string> = {
      abierta: 'Abierta',
      seleccion_unica: 'Seleccion unica',
      seleccion_multiple: 'Seleccion multiple',
      escala: 'Escala 1-5',
      si_no: 'Si / No'
    };
    return labels[tipo] || tipo;
  }

  enviarRespuestas(): void {
    this.submitted = true;
    if (!this.esValido()) {
      Swal.fire({
        icon: 'warning',
        title: 'Campos incompletos',
        text: 'Por favor responde todas las preguntas antes de enviar.',
        confirmButtonColor: '#4e73df'
      });
      return;
    }

    if (!this.nombre_respondente.trim() || !this.placa_vehiculo.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Datos incompletos',
        text: 'Por favor ingresa tu nombre y placa del vehículo.',
        confirmButtonColor: '#4e73df'
      });
      return;
    }

    this.isEnviando = false;

    Swal.fire({
      icon: 'question',
      title: '¿Confirmar envío?',
      html: `¿Deseas enviar tus respuestas?<br><br>
             <strong>Nombre:</strong> ${this.nombre_respondente.trim()}<br>
             <strong>Placa:</strong> ${this.placa_vehiculo.trim().toUpperCase()}`,
      showCancelButton: true,
      confirmButtonText: 'Sí, enviar',
      cancelButtonText: 'Revisar',
      confirmButtonColor: '#4e73df',
      cancelButtonColor: '#6c757d'
    }).then((result: any) => {
      if (!result.isConfirmed) return;

      this.isEnviando = true;
      const payload = {
        encuesta_id: this.encuestaId,
        nit: this.nit,
        nombre_respondente: this.nombre_respondente.trim(),
        placa_vehiculo: this.placa_vehiculo.trim().toUpperCase(),
        respuestas: this.respuestas
      };

      this.http.post(`${environment.apiUrl}/api/public/encuesta/${this.nit}/${this.encuestaId}/respuestas`, payload)
        .subscribe({
          next: () => {
            this.isEnviando = false;
            Swal.fire({
              icon: 'success',
              title: '¡Encuesta enviada!',
              text: 'Tus respuestas han sido registradas correctamente. ¡Gracias por tu participación!',
              confirmButtonText: 'Aceptar',
              confirmButtonColor: '#4e73df'
            }).then(() => {
              this.limpiarFormulario();
              this.enviado = true;
            });
          },
          error: (err) => {
            this.isEnviando = false;
            Swal.fire({
              icon: 'error',
              title: 'Error al enviar',
              text: err?.error?.message || 'No se pudo enviar la respuesta. Intenta de nuevo.',
              confirmButtonColor: '#f64e60'
            });
          }
        });
    });
  }

  get progreso(): number {
    const total = this.encuesta?.preguntas?.length ?? 0;
    if (total === 0) return 0;
    const respondidas = this.respuestas.filter((_, i) => this.esRespondida(i)).length;
    return Math.round((respondidas / total) * 100);
  }
}